import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import { FaRedo, FaTrashAlt } from "react-icons/fa";
import { toast } from "react-toastify";
import api from "../Api";
import ScanSummary from "./ScanSummary";
import NotScannedDialog from "./NotScannedDialog";
import { getAppDateString } from "../utils/date";

const ALL = "ALL";

export default function ReturnClaimsPage() {
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState("");
  const [selectedDate, setSelectedDate] = useState(getAppDateString());
  const [nsOpen, setNsOpen] = useState(false);
  const [nsGroup, setNsGroup] = useState("");
  const [scanValue, setScanValue] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const scanRef = useRef(null);

  const total = useMemo(() => rows.length, [rows]);
  const canScan = selectedAccount && selectedAccount !== ALL;

  // Load active accounts for the dropdown (same source as the scan page).
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/api/accounts?active_only=true");
        setAccounts(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("Failed to load accounts:", err);
        toast.error("Failed to load accounts");
      }
    })();
  }, []);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/api/return-claims", {
        params: {
          date: selectedDate,
          account: selectedAccount || ALL,
        },
      });
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to fetch return claims:", err);
      setError(err.response?.data?.error || "Failed to load return claims.");
    } finally {
      setLoading(false);
    }
  }, [selectedAccount, selectedDate]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  useEffect(() => {
    if (saving || !canScan) return undefined;

    const frame = window.requestAnimationFrame(() => {
      scanRef.current?.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [saving, canScan, selectedAccount]);

  const submitScan = async () => {
    const tracking = scanValue.trim();
    if (!tracking) return;
    if (!canScan) {
      toast.warn("Select a specific account before scanning.");
      return;
    }

    setSaving(true);
    try {
      const res = await api.post("/api/return-claims", {
        tracking_id: tracking,
        account_name: selectedAccount,
        date: selectedDate,
        source: "web",
      });
      const data = res.data || {};
      if (data.status === "duplicate") {
        toast.warn(data.message || "Already scanned.");
      } else if (data.success) {
        toast.success(data.message || "Return claim stored.");
      } else {
        toast.error(data.message || "Failed to store return claim.");
      }
      await fetchRows();
    } catch (err) {
      console.error("Return claim scan failed:", err);
      toast.error(err.response?.data?.message || "Failed to store return claim.");
    } finally {
      setSaving(false);
      setScanValue("");
    }
  };

  const deleteRow = async (row) => {
    if (!window.confirm(`Delete return claim "${row.tracking_id}"?`)) return;
    try {
      await api.delete(`/api/return-claims/${row.id}`);
      toast.success("Return claim deleted");
      fetchRows();
    } catch (err) {
      console.error("Failed to delete return claim:", err);
      toast.error(err.response?.data?.message || "Failed to delete return claim");
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Toolbar disableGutters sx={{ gap: 2, flexWrap: "wrap", mb: 2 }}>
        <Box sx={{ flex: 1, minWidth: 220 }}>
          <Typography variant="h5" sx={{ fontWeight: "bold" }}>
            Return Claims
          </Typography>
          <Typography color="text.secondary">
            {total} claim{total === 1 ? "" : "s"}
            {selectedAccount && selectedAccount !== ALL ? ` · ${selectedAccount}` : ""} on {selectedDate}
          </Typography>
        </Box>

        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Account</InputLabel>
          <Select
            label="Account"
            value={selectedAccount}
            onChange={(e) => setSelectedAccount(e.target.value)}
          >
            <MenuItem value={ALL}>-- All Accounts --</MenuItem>
            {accounts.map((a) => (
              <MenuItem key={a.id} value={a.name}>{a.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Date"
          type="date"
          size="small"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 170 }}
        />

        <Button variant="contained" startIcon={<FaRedo />} onClick={fetchRows} disabled={loading}>
          Refresh
        </Button>
      </Toolbar>

      {/* Scan input bar */}
      <Paper sx={{ p: 2, mb: 2, bgcolor: "#f8f8f8" }}>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
          <TextField
            fullWidth
            inputRef={scanRef}
            autoFocus
            label={canScan ? `Scan return claim for ${selectedAccount}` : "Select an account to scan"}
            placeholder="Scan or type tracking ID, then press Enter"
            value={scanValue}
            disabled={!canScan || saving}
            onChange={(e) => setScanValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submitScan(); } }}
            sx={{ flex: 1, minWidth: 240, bgcolor: "white" }}
          />
          <Button
            variant="contained"
            color="success"
            onClick={submitScan}
            disabled={!canScan || saving || !scanValue.trim()}
          >
            Add
          </Button>
        </Box>
        {!canScan && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Choose a specific account above to start scanning return claims.
          </Typography>
        )}
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f3f4f6" }}>
              <TableCell>Scanned On</TableCell>
              <TableCell>Tracking ID</TableCell>
              <TableCell>Account</TableCell>
              <TableCell>Order ID</TableCell>
              <TableCell>SKU</TableCell>
              <TableCell align="right">Qty</TableCell>
              <TableCell>Source</TableCell>
              <TableCell align="center">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  {loading ? "Loading..." : "No return claims found."}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{row.scanned_on || "-"}</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>{row.tracking_id}</TableCell>
                  <TableCell>{row.account_name || "-"}</TableCell>
                  <TableCell>{row.order_id || "-"}</TableCell>
                  <TableCell>{row.sku || "-"}</TableCell>
                  <TableCell align="right">{row.quantity ?? "-"}</TableCell>
                  <TableCell>{row.source || "web"}</TableCell>
                  <TableCell align="center">
                    <IconButton color="error" size="small" onClick={() => deleteRow(row)}>
                      <FaTrashAlt />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* the day's scan position, as on the other pages */}
      <ScanSummary
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
        readOnlyDate={true}
        onOpenNotScanned={(g) => { setNsGroup(g || ""); setNsOpen(true); }}
      />
      {nsOpen && (
        <NotScannedDialog open={nsOpen} onClose={() => setNsOpen(false)}
                          accounts={[]} initialGroup={nsGroup} />
      )}
    </Box>
  );
}
